import { MultiSelect, type MultiSelectProps } from "@mantine/core";
import { useCallback } from "react";
import { useInputProps } from "src/generator/hooks/useInputProps";
import useGetSelectData from "../../hooks/useGetSelectData";
import useTriggerInlineSave from "../../../hooks/useTriggerInlineSave";
import { useDispatch } from "../../../redux/hooks";
import { setEditing } from "../../../redux/slices/entrySlice";
import CustomSelectItem from "./CustomSelectItem";
import type { ComboboxItem } from "@mantine/core";
import { mantineRenderOption } from "src/utils/mantineRenderOption";

interface ICustomMultiSelect extends Omit<MultiSelectProps, "onChange"> {
  value: any;
  id: string;
  data: any[];
  required: boolean;
  tag?: string;
  rowIndex?: number;
  colIndex?: number;
  sectionIndex?: number;
  onChange?: (value: string[]) => void;
  width?: string;
  withLabel?: boolean;
}

const CustomMultiSelect = ({
  value,
  id,
  data,
  required,
  onChange,
  width,
  withLabel,
  sectionIndex,
  ...rest
}: ICustomMultiSelect) => {
  const dispatch = useDispatch();
  const triggerSave = useTriggerInlineSave(sectionIndex);
  const { onChange: defaultOnChange, ...inputProps } = useInputProps(
    id,
    value,
    required,
  );
  const getData = useGetSelectData({ data, value });

  const handleChange = useCallback(
    (values: string[]) => {
      // For single selection, we handle one item, for multi we handle multiple
      if (values.length === 0) {
        defaultOnChange([]);
      } else {
        const items = values
          .map(val => getData().find(i => i.value === val))
          .filter(Boolean);

        // If it's a single value, use the original format; if multiple, use array
        if (items.length === 1) {
          defaultOnChange({ key: items[0].chrisKey, value: items[0].label });
        } else {
          defaultOnChange(
            items.map(item => ({ key: item.chrisKey, value: item.label })),
          );
        }
      }

      if (onChange) onChange(values);
      triggerSave();
      dispatch(setEditing(true));
    },
    [getData, defaultOnChange, onChange, triggerSave, dispatch],
  );

  const getValue = (): string[] => {
    const data = getData();
    if (!inputProps.value) return [];

    // If it's already an array of strings, return it
    if (
      Array.isArray(inputProps.value) &&
      typeof inputProps.value[0] === "string"
    ) {
      return inputProps.value;
    }

    // If it's a single object with key/value (from single select), convert to array
    if (
      typeof inputProps.value === "object" &&
      inputProps.value.key &&
      inputProps.value.value
    ) {
      const item = data.find(
        i =>
          i.chrisKey === inputProps.value.key &&
          i.label === inputProps.value.value,
      );
      return item ? [String(item.value)] : [];
    }

    // If it's an array of objects with key/value, convert to array of strings
    if (Array.isArray(inputProps.value)) {
      return inputProps.value
        .map((item: any) => {
          const dataItem = data.find(
            i => i.chrisKey === item.key && i.label === item.value,
          );
          return dataItem ? String(dataItem.value) : null;
        })
        .filter(Boolean);
    }

    return [];
  };

  return (
    <MultiSelect
      label={withLabel ? "Select multiple items" : null}
      w={width ? Number(width) * 12 - 20 : 200}
      data={getData()}
      onChange={handleChange}
      comboboxProps={{ withinPortal: true, position: "top", zIndex: 1000 }}
      {...rest}
      {...inputProps}
      value={getValue()}
      renderOption={mantineRenderOption(CustomSelectItem)}
    />
  );
};

export default CustomMultiSelect;